/**
 * Loans (FR-LOAN-01 to FR-LOAN-12).
 *
 * Pure: the repayment schedule, the amortised position at any date, and the foreclosure
 * calculator. Nothing here is specific to the one seeded loan — FR-LOAN-10 requires
 * further loans to work the same way, so every function takes the loan and its schedule
 * rather than reaching for a constant.
 *
 * **FR-LOAN-04, and it is the load-bearing rule of this file: the lender's repayment
 * schedule is authoritative and the app never derives its own amortisation.** The KFS
 * Annex C disagrees with the schedule (first instalment 25,808 against 25,471, total
 * interest 345,803 against 345,466) and the KFS itself says Annex C is illustrative.
 * Recomputing an amortisation from rate and tenure would land a rupee or two away on most
 * rows, and those rupees are exactly what a foreclosure quote is made of. So the schedule
 * is transcribed from the PDF (`scripts/build-loan-schedule.py`) and read, never rebuilt.
 */

import { isValidDateKey, monthsElapsed, type DateKey } from './dates';
import { roundHalfUp, type Paise } from './money';
import scheduleFixture from './__fixtures__/loan-schedule.json';

export interface Instalment {
  instalmentNo: number;
  dueDateKey: DateKey;
  amountPaise: Paise;
  principalPaise: Paise;
  interestPaise: Paise;
  /** Principal still owed **after** this instalment, as the lender states it. */
  outstandingPaise: Paise;
}

export interface Loan {
  id: string;
  name: string;
  /** The `accounts/{id}` of type `loan` this loan settles against (FR-LOAN-01). */
  accountId: string;
  lender: string;
  loanAccountNumber: string;
  asset: string;

  sanctionedPaise: Paise;
  /** Add-ons rolled into the sanctioned amount, itemised so the figure is explicable. */
  addOns: { label: string; amountPaise: Paise }[];

  disbursedOn: DateKey;
  agreementDate: DateKey;

  /** Percent, one decimal — 12.0 means 12% fixed. */
  interestRatePercent: number;
  aprExclInsurancePercent: number;
  aprInclInsurancePercent: number;

  tenureMonths: number;
  emiPaise: Paise;
  emiDayOfMonth: number;

  totalPayablePaise: Paise;
  repaymentMode: string;
  oneTimeFees: { label: string; amountPaise: Paise }[];

  archived: boolean;
}

const rupees = (amount: number): Paise => Math.round(amount * 100);

/**
 * The one seeded loan (FR-LOAN-02, FR-LOAN-03).
 *
 * "Car Loan" and "Mahindra Fin LL" in the workbook are this same loan under two labels;
 * the importer merges them (FR-IMP-16). The workbook's "Car Loan 1,000,000" is a stale
 * hand-typed value — neither the sanctioned amount nor the outstanding — and is not
 * imported (FR-LOAN-12).
 */
export const MAHINDRA_CAR_LOAN: Loan = {
  id: 'mahindra-car-loan',
  name: 'Car Loan — Mahindra',
  accountId: 'mahindra-car-loan',
  lender: 'Mahindra & Mahindra Financial Services',
  loanAccountNumber: '001000280261',
  asset: 'MG Windsor EV Essence Pro',

  sanctionedPaise: rupees(1_023_938),
  addOns: [
    { label: 'Arogya Suraksha', amountPaise: rupees(4_999) },
    { label: 'Loan Suraksha', amountPaise: rupees(18_939) },
  ],

  disbursedOn: '2025-07-28',
  agreementDate: '2025-07-27',

  interestRatePercent: 12.0,
  aprExclInsurancePercent: 12.4,
  aprInclInsurancePercent: 13.45,

  tenureMonths: 60,
  emiPaise: rupees(22_778),
  emiDayOfMonth: 5,

  totalPayablePaise: rupees(1_369_404),
  repaymentMode: 'ACH',
  oneTimeFees: [
    { label: 'Processing', amountPaise: rupees(6_000) },
    { label: 'Documentation', amountPaise: rupees(2_000) },
    { label: 'Service', amountPaise: rupees(1_200) },
    { label: 'Stamp duty', amountPaise: rupees(100) },
  ],

  archived: false,
};

/** The lender's schedule, as extracted from the PDF. 60 instalments. */
export const MAHINDRA_SCHEDULE: Instalment[] = scheduleFixture as Instalment[];

// ---------------------------------------------------------------------------
// Reading the schedule
// ---------------------------------------------------------------------------

export interface ScheduleTotals {
  instalments: number;
  principalPaise: Paise;
  interestPaise: Paise;
  totalPayablePaise: Paise;
  firstPaise: Paise;
  lastPaise: Paise;
}

export function scheduleTotals(schedule: Instalment[]): ScheduleTotals {
  const principalPaise = schedule.reduce((sum, i) => sum + i.principalPaise, 0);
  const interestPaise = schedule.reduce((sum, i) => sum + i.interestPaise, 0);
  return {
    instalments: schedule.length,
    principalPaise,
    interestPaise,
    totalPayablePaise: principalPaise + interestPaise,
    firstPaise: schedule[0]?.amountPaise ?? 0,
    lastPaise: schedule[schedule.length - 1]?.amountPaise ?? 0,
  };
}

/** Instalments due on or before `asAt`. */
export function instalmentsDueBy(schedule: Instalment[], asAt: DateKey): Instalment[] {
  return schedule.filter((i) => i.dueDateKey <= asAt);
}

/**
 * Outstanding principal at a date, straight from the lender's schedule.
 *
 * Before the first instalment falls due the answer is the sanctioned amount, which is why
 * this needs the loan as well as the schedule — the schedule's own rows only start after
 * the first payment.
 */
export function outstandingPrincipalAt(
  loan: Pick<Loan, 'sanctionedPaise'>,
  schedule: Instalment[],
  asAt: DateKey,
): Paise {
  const due = instalmentsDueBy(schedule, asAt);
  return due.length === 0 ? loan.sanctionedPaise : due[due.length - 1].outstandingPaise;
}

/** The instalment falling on a given date, if any. Used to split an imported EMI. */
export function instalmentOn(schedule: Instalment[], dateKey: DateKey): Instalment | null {
  return schedule.find((i) => i.dueDateKey === dateKey) ?? null;
}

/**
 * How an EMI paid on `dateKey` splits (FR-LOAN-06).
 *
 * Matched on the due date rather than by counting payments, because a missed or early
 * month would silently shift every later split by one instalment. Returns null when no
 * instalment falls on that date, and the caller then treats the payment as an
 * undifferentiated transfer rather than guessing at a split.
 */
export function emiSplitOn(
  schedule: Instalment[],
  dateKey: DateKey,
): { instalmentNo: number; principalPaise: Paise; interestPaise: Paise } | null {
  const instalment = instalmentOn(schedule, dateKey);
  if (!instalment) return null;
  return {
    instalmentNo: instalment.instalmentNo,
    principalPaise: instalment.principalPaise,
    interestPaise: instalment.interestPaise,
  };
}

// ---------------------------------------------------------------------------
// Foreclosure (FR-LOAN-07, FR-LOAN-08)
// ---------------------------------------------------------------------------

export const GST_PERCENT = 18;

export type ChargeKind = 'foreclosure' | 'part-payment';

export interface QuoteInput {
  loan: Pick<Loan, 'agreementDate' | 'sanctionedPaise'>;
  schedule: Instalment[];
  asAt: DateKey;
  kind?: ChargeKind;
  /** Part-payment only. Foreclosure is always the whole outstanding principal. */
  partPaymentPaise?: Paise;
  /** Overdue amount attracting the 3%/month penal charge, and for how many months. */
  overduePaise?: Paise;
  overdueMonths?: number;
  /** Bounced instalments, at ₹500 each. */
  bounceCount?: number;
  /** The date of the first EMI. Defaults to the first row of the schedule. */
  firstEmiDate?: DateKey;
}

export interface Quote {
  asAt: DateKey;
  kind: ChargeKind;
  instalmentsPaid: number;
  outstandingPrincipalPaise: Paise;
  /** What the percentage is charged on: all of it, or the part-payment. */
  chargeBasePaise: Paise;
  ratePercent: number;
  /** Why that rate, in words — the rule is a cliff and the user should see which side. */
  rateReason: string;
  chargePaise: Paise;
  penalPaise: Paise;
  bouncePaise: Paise;
  /** GST at 18%, on the charges only and **never on the principal** (FR-LOAN-07). */
  gstPaise: Paise;
  payoffPaise: Paise;
}

export const PENAL_PERCENT_PER_MONTH = 3;
export const BOUNCE_CHARGE_PAISE = 50_000; // ₹500

/** Whole rupees. A tax invoice is stated in rupees, and this is what makes the
 *  acceptance total in FR-LOAN-08 come out exactly. See the note on `gstOn`. */
function toWholeRupees(paise: Paise): Paise {
  return roundHalfUp(paise / 100) * 100;
}

/**
 * GST on a charge.
 *
 * **Rounded to whole rupees, and that rounding is not cosmetic.** FR-LOAN-08's acceptance
 * figures are charge 52,725, GST 9,490, payoff 940,966 — and those three do not add up:
 * 878,750 + 52,725 + 9,490 is 940,965, one rupee short of the stated payoff.
 *
 * The cause is that 18% of 52,725 is **9,490.50 exactly**. Truncating it gives the quoted
 * 9,490; rounding it gives 9,491, and 878,750 + 52,725 + 9,491 = **940,966**, which is the
 * quoted payoff. So the payoff figure is right and the GST figure was transcribed down.
 * Rounding to whole rupees reproduces the total the lender would actually quote.
 */
export function gstOn(chargePaise: Paise): Paise {
  return toWholeRupees((chargePaise * GST_PERCENT) / 100);
}

/**
 * The charge rate and the reason for it (FR-LOAN-07).
 *
 * Both rules are cliffs measured in whole months, and they are measured from *different*
 * dates — foreclosure from the agreement date, part-payment from the first EMI. Getting
 * that wrong is a silent 3-percentage-point error on a six-figure balance.
 */
export function chargeRate(
  kind: ChargeKind,
  asAt: DateKey,
  anchor: DateKey,
): { ratePercent: number; rateReason: string } {
  const months = monthsElapsed(anchor, asAt);

  if (kind === 'foreclosure') {
    return months < 24
      ? { ratePercent: 6, rateReason: `${months} months from the agreement date — within 24, so 6%` }
      : { ratePercent: 3, rateReason: `${months} months from the agreement date — past 24, so 3%` };
  }

  // Part-payment: 5% between 13 and 24 months of the first EMI, 3% after. Below 13
  // months the lender does not permit one; the rate is reported as 5% and the caller
  // is told why rather than being handed a number with no context.
  if (months < 13) {
    return { ratePercent: 5, rateReason: `${months} months from the first EMI — part-payment is not normally permitted before 13` };
  }
  return months <= 24
    ? { ratePercent: 5, rateReason: `${months} months from the first EMI — within 13–24, so 5%` }
    : { ratePercent: 3, rateReason: `${months} months from the first EMI — past 24, so 3%` };
}

export class LoanError extends Error {}

/**
 * A foreclosure or part-payment quote for any chosen date (FR-LOAN-07).
 *
 * Every figure is derived: the outstanding principal comes from the lender's schedule,
 * the rate from the elapsed-months rule, and GST from the charge. Nothing is typed in,
 * which is what makes the acceptance test in FR-LOAN-08 meaningful rather than a
 * restatement of a constant.
 */
export function quote(input: QuoteInput): Quote {
  if (!isValidDateKey(input.asAt)) throw new LoanError('A valid date is required.');

  const kind = input.kind ?? 'foreclosure';
  const schedule = input.schedule;
  const paid = instalmentsDueBy(schedule, input.asAt);
  const outstandingPrincipalPaise = outstandingPrincipalAt(input.loan, schedule, input.asAt);

  const chargeBasePaise =
    kind === 'part-payment' ? (input.partPaymentPaise ?? 0) : outstandingPrincipalPaise;
  if (kind === 'part-payment' && chargeBasePaise > outstandingPrincipalPaise) {
    throw new LoanError('A part-payment cannot exceed the outstanding principal.');
  }

  const anchor =
    kind === 'foreclosure'
      ? input.loan.agreementDate
      : (input.firstEmiDate ?? schedule[0]?.dueDateKey ?? input.loan.agreementDate);

  const { ratePercent, rateReason } = chargeRate(kind, input.asAt, anchor);

  const chargePaise = toWholeRupees((chargeBasePaise * ratePercent) / 100);
  const penalPaise = toWholeRupees(
    ((input.overduePaise ?? 0) * PENAL_PERCENT_PER_MONTH * (input.overdueMonths ?? 0)) / 100,
  );
  const bouncePaise = (input.bounceCount ?? 0) * BOUNCE_CHARGE_PAISE;

  // GST applies to the charges, never to the principal being repaid.
  const gstPaise = gstOn(chargePaise + penalPaise + bouncePaise);

  return {
    asAt: input.asAt,
    kind,
    instalmentsPaid: paid.length,
    outstandingPrincipalPaise,
    chargeBasePaise,
    ratePercent,
    rateReason,
    chargePaise,
    penalPaise,
    bouncePaise,
    gstPaise,
    payoffPaise:
      (kind === 'part-payment' ? chargeBasePaise : outstandingPrincipalPaise) +
      chargePaise +
      penalPaise +
      bouncePaise +
      gstPaise,
  };
}
